import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, Shield, TrendingUp, Users, ChevronRight, Sparkles, Coins, Flame } from "lucide-react";
import { PAGE_NAME } from "../../types";

const HomeGuestView: React.FC = () => {
  const navigate = useNavigate();
  const [estimatedEarnings, setEstimatedEarnings] = useState<number | null>(null);

  const features = [
    {
      icon: <Zap size={20} />,
      title: "Instant Grants",
      desc: "Claim free ATH tokens every 2 hours",
      gradient: "from-cyan-500 to-blue-500",
      bg: "bg-cyan-50",
    },
    {
      icon: <TrendingUp size={20} />,
      title: "Earn & Grow",
      desc: "Watch ads, invite friends, stake & multiply",
      gradient: "from-emerald-500 to-teal-500",
      bg: "bg-emerald-50",
    },
    {
      icon: <Shield size={20} />,
      title: "Secure Wallet",
      desc: "Blockchain-powered Pi Network integration",
      gradient: "from-purple-500 to-indigo-500",
      bg: "bg-purple-50",
    },
    {
      icon: <Users size={20} />,
      title: "Community",
      desc: "Join thousands of active Arthereum holders",
      gradient: "from-amber-500 to-orange-500",
      bg: "bg-amber-50",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="px-4 pt-8 pb-4">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center gap-3 mb-2"
          >
            <div className="w-10 h-10 bg-gradient-to-br from-cyan-400 via-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
              <Zap size={20} className="text-white" />
            </div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
              Arthereum Wallet
            </h1>
          </motion.div>
        </div>

        {/* Value proposition */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mx-4 mb-4"
        >
          <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl px-4 py-2.5 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
              <span className="text-white text-xs font-bold">Free ATH grants every 2 hours, on Pi Network</span>
            </div>
          </div>
        </motion.div>

        {/* Welcome Hero Card */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mx-4 mb-4 p-6 rounded-2xl bg-gradient-to-br from-slate-900 via-purple-900 to-indigo-900 text-white relative overflow-hidden shadow-xl"
        >
          {/* Decorative glow */}
          <div className="absolute -top-16 -right-16 w-40 h-40 bg-cyan-500/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-12 -left-12 w-32 h-32 bg-purple-500/20 rounded-full blur-3xl" />
          
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={16} className="text-cyan-300" />
              <span className="text-[10px] font-bold uppercase tracking-widest text-cyan-300">
                Welcome
              </span>
            </div>
            <h2 className="text-2xl font-bold mb-2 leading-tight">
              Start Earning <br />
              <span className="bg-gradient-to-r from-cyan-300 to-blue-300 bg-clip-text text-transparent">
                Arthereum Tokens
              </span>
            </h2>
            <p className="text-white/60 text-sm mb-6 leading-relaxed">
              Join our growing community and claim free ATH grants, compete on leaderboards, and build your crypto portfolio.
            </p>

            {/* Login CTA Button */}
            <button
              onClick={() => navigate(PAGE_NAME.LOGIN)}
              className="group w-full h-14 flex items-center justify-center gap-3 bg-gradient-to-r from-cyan-500 via-blue-600 to-purple-600 hover:from-cyan-400 hover:via-blue-500 hover:to-purple-500 text-white font-semibold rounded-xl shadow-lg hover:shadow-cyan-500/25 transition-all duration-300 border border-white/20"
            >
              <span className="group-hover:translate-x-0.5 transition-transform duration-200">
                Get Started — It's Free
              </span>
              <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform duration-200" />
            </button>
          </div>
        </motion.div>

        {/* Earnings Calculator — Hook */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mx-4 mb-4 p-4 rounded-2xl bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 shadow-sm"
        >
          <div className="flex items-center gap-2 mb-3">
            <Coins size={16} className="text-amber-500" />
            <span className="text-sm font-black text-amber-800">How much can you earn?</span>
          </div>
          <div className="flex gap-2 mb-3">
            {[5, 15, 30].map((mins) => (
              <button
                key={mins}
                onClick={() => {
                  // Rough calculation: grants + ads + streaks
                  const grants = (mins / 120) * 3.5; // ~3.5 ATH per grant avg
                  const ads = (mins / 3) * 0.75; // ad every 3 min avg reward
                  const streak = 2; // daily streak avg
                  setEstimatedEarnings(+(grants + ads + streak).toFixed(1));
                }}
                className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${
                  estimatedEarnings !== null
                    ? "bg-amber-100 text-amber-700 hover:bg-amber-200"
                    : "bg-amber-200 text-amber-800 hover:bg-amber-300"
                }`}
              >
                {mins} min/day
              </button>
            ))}
          </div>
          {estimatedEarnings !== null && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              className="text-center"
            >
              <p className="text-2xl font-black text-amber-700">
                ~{estimatedEarnings} ATH/day
              </p>
              <p className="text-[10px] text-amber-600 mt-1">
                That's ~{(estimatedEarnings * 7).toFixed(0)} ATH/week • ~{(estimatedEarnings * 30).toFixed(0)} ATH/month
              </p>
              <button
                onClick={() => navigate(PAGE_NAME.LOGIN)}
                className="mt-3 w-full py-2.5 rounded-lg bg-amber-500 text-white font-bold text-sm active:scale-95 transition-transform"
              >
                Start Earning Now →
              </button>
            </motion.div>
          )}
        </motion.div>

        {/* Features Grid */}
        <div className="px-4 pb-4">
          <div className="grid grid-cols-2 gap-3">
            {features.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.2 + i * 0.08 }}
                className={`${f.bg} border border-gray-100 rounded-xl p-4 cursor-pointer hover:shadow-md transition-all duration-200`}
                onClick={() => navigate(PAGE_NAME.LOGIN)}
              >
                <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${f.gradient} flex items-center justify-center text-white mb-3 shadow-sm`}>
                  {f.icon}
                </div>
                <h3 className="font-bold text-gray-900 text-sm mb-0.5">{f.title}</h3>
                <p className="text-[11px] text-gray-500 leading-snug">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Stats Strip */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="mx-4 mb-4 p-4 rounded-xl bg-white border border-gray-100 shadow-sm"
        >
          <div className="flex items-center justify-around text-center">
            <div>
              <div className="text-lg font-black text-gray-900">2h</div>
              <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">Grant cycle</div>
            </div>
            <div className="w-px h-8 bg-gray-100" />
            <div>
              <div className="text-lg font-black text-gray-900">30</div>
              <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">Paid ads / day</div>
            </div>
            <div className="w-px h-8 bg-gray-100" />
            <div>
              <div className="text-lg font-black text-gray-900">200 ATH</div>
              <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">Per friend</div>
            </div>
          </div>
        </motion.div>

        {/* Urgency Banner */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3, delay: 0.7 }}
          className="mx-4 mb-4"
        >
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center shrink-0">
              <Flame size={16} className="text-red-500" />
            </div>
            <div className="flex-1">
              <p className="text-xs font-bold text-red-800">Welcome bonus</p>
              <p className="text-[10px] text-red-600">Join through a friend's link and start with 100 ATH.</p>
            </div>
            <button
              onClick={() => navigate(PAGE_NAME.LOGIN)}
              className="px-3 py-1.5 rounded-lg bg-red-500 text-white text-[10px] font-bold shrink-0"
            >
              GO
            </button>
          </div>
        </motion.div>

        {/* Bottom Login Nudge */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.6 }}
          className="mx-4 mb-8"
        >
          <button
            onClick={() => navigate(PAGE_NAME.LOGIN)}
            className="w-full p-4 rounded-xl border-2 border-dashed border-gray-200 hover:border-blue-300 bg-white hover:bg-blue-50/50 transition-all duration-200 group"
          >
            <p className="text-sm text-gray-500 group-hover:text-blue-600 transition-colors">
              Already have an account?{" "}
              <span className="font-bold text-gray-900 group-hover:text-blue-700">Log in →</span>
            </p>
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default HomeGuestView;
